import { FC, useCallback } from 'react';

import Card from 'components/_core/Card';
import SunshineCard from 'components/_core/SunshineCard';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import SunshineTag from 'components/_core/SunshineTag';
import Column from 'components/Column';
import getVerificationSteps from 'features/Onboarding/common/getVerificationSteps';
import getBeneficiariesStep from 'features/Onboarding/common/getVerificationSteps/getBeneficiariesStep';
import { VerificationStepType } from 'features/Onboarding/common/getVerificationSteps/types';
import { VerificationStatus } from 'features/Onboarding/common/useVerificationStatus/types';
import Header from 'features/Onboarding/components/Header';

import locales from './locales';
import {
  SectionDescription,
  SectionTitle,
  SectionWrapper,
  UnderReviewIcon,
} from './styles';
import { MultiKycContentProps } from './types';

const CURRENT_USER_STEPS: VerificationStepType[] = [
  'id',
  'address',
  'capital-deposit',
  'shine-start',
];

/**
 * Given a multiKycStatus,
 * returns a sentence describing the global beneficiaries' advancement.
 */
const getBeneficiariesIntroduction = (
  status: VerificationStatus['multiKycStatus'],
) => {
  switch (status) {
    case 'complete':
      return locales.beneficiariesValidText;

    case 'pending':
      return locales.beneficiariesPendingText;

    default:
      return locales.beneficiariesWaitingText;
  }
};

const MultiKycContent: FC<MultiKycContentProps> = ({
  setCurrentPage,
  status,
}) => {
  const onBeneficiariesPress = useCallback(() => {
    setCurrentPage('beneficiaries-docs');
  }, [setCurrentPage]);

  const currentUserSteps = getVerificationSteps(status, CURRENT_USER_STEPS);
  const beneficiariesStep = getBeneficiariesStep(status);

  return (
    <>
      {['pending', 'complete'].includes(status.multiKycStatus) ? (
        <Header
          description={
            <>
              <UnderReviewIcon name="timer" />
              {locales.delay}
            </>
          }
          title={locales.titleUnderReview}
        />
      ) : (
        <Header title={locales.titleWaitingBeneficiaries} />
      )}

      <SectionWrapper>
        <SectionTitle>{locales.primaryApplicantTitle}</SectionTitle>

        <SectionDescription>
          {/* On this page, the current user can be either pending or complete. */}
          {status.isCurrentUserComplete
            ? locales.primaryApplicantValidLabel
            : locales.primaryApplicantPendingLabel}
        </SectionDescription>

        <Card padding="0">
          <Column>
            <SunshineCard.Group>
              {currentUserSteps.map(({ nextPage, ...stepProps }) => (
                <SunshineCard
                  isDisabled
                  key={stepProps.label}
                  title={stepProps.label}
                >
                  <SunshineCard.Slot name="aside">
                    <SquircleIcon name="timer" />
                  </SunshineCard.Slot>

                  <SunshineCard.Slot name="action">
                    <SunshineTag>{locales.documentSent}</SunshineTag>
                  </SunshineCard.Slot>
                </SunshineCard>
              ))}
            </SunshineCard.Group>
          </Column>
        </Card>
      </SectionWrapper>

      <SectionWrapper>
        <SectionTitle>
          {['complete', 'pending'].includes(status.multiKycStatus)
            ? locales.beneficiariesDoneTitle
            : locales.beneficiariesWaitingTitle}
        </SectionTitle>

        <SectionDescription>
          {getBeneficiariesIntroduction(status.multiKycStatus)}
        </SectionDescription>

        <SunshineCard
          alignItems="center"
          onClick={onBeneficiariesPress}
          title={beneficiariesStep.label}
        >
          <SunshineCard.Slot name="aside">
            {['complete', 'pending'].includes(beneficiariesStep.status) ? (
              <SquircleIcon colorScheme="green" name="tick" />
            ) : (
              <SquircleIcon name="team" />
            )}
          </SunshineCard.Slot>

          <SunshineCard.Content>
            <SunshineCard.Text>{beneficiariesStep.notes}</SunshineCard.Text>
          </SunshineCard.Content>

          <SunshineCard.Slot name="action">
            <SunshineCard.SunshineIcon name="chevron-right" />
          </SunshineCard.Slot>
        </SunshineCard>
      </SectionWrapper>
    </>
  );
};

export default MultiKycContent;
